/** 셀러가 **자기 상품이 무엇인지 말한 것**. `A-26c`(앱) · `W-26` 오른쪽 패널(웹).
 *  (PRD `7.6.9b` · 법무 `docs/LEGAL_IP_GUIDE.md` `6.2.1`(2) · `13.1` `O-72`)
 *
 * ## ⭐ 고르는 칸이 아니라 **진술의 기록이 남는 칸**이다
 *
 * 법무가 둘을 갈랐다(`6.2.1`(2)). **카테고리를 고르기만 하는 칸은 사실의 분류**라 막을
 * 근거는 생기지만 **약속이 남지 않는다** — 셀러가 화장품을 다른 값으로 골라 지나갔을 때
 * 걸 수 있는 것이 「잘못 골랐다」뿐이고 그것은 약속 위반이 아니다.
 *
 * **그래서 고를 값 자체가 진술문이다.** `화장품입니다` 는 분류가 아니라 **셀러가 하는 말**
 * 이고, 그래서 **행위 기록**이 남는다. 아래 `sentence` 칸이 그 말을 그대로 들고 있는 이유가
 * 그것이다 — 코드값(`COSMETIC`)만으로는 **무엇을 말했는지**가 아니라 **우리가 무엇으로
 * 분류했는지**만 남는다.
 *
 * ⛔ **진술은 책임을 옮기지 않는다. 증거를 남긴다**(법무 `6.2.1`(2)ⓒ). 셀러가 아니라고
 * 말하고 지나가면 **「아니라고 진술했다」가 기록 옆에 함께 남을 뿐**이고, 우리 지위가
 * 어떻게 평가되는지는 **그대로 열려 있다**. ⛔ **이 문단을 지우면 다음 사람이 「셀러가
 * 진술했으니 우리 책임이 아니다」로 읽는다.**
 *
 * ## ⛔ `internal-category.ts` 와 다른 축이다
 *
 * | | 이 파일 | `internal-category.ts` |
 * |---|---|---|
 * | 누가 정하는가 | **셀러** | 우리(모델) |
 * | 셀러가 보는가 | **본다.** 진술문 그대로 | ⛔ 안 본다 |
 * | 무엇을 가르는가 | **상품명을 만들어 드리는가** | 우리 데이터 집계 |
 *
 * ⛔ **스마트스토어 등록용 카테고리도 아니다.** 그쪽은 2단계(`13.2` `O-27`)이고 **성격이
 * 다른 칸이라 하나가 다른 하나를 대신하지 않는다**(PRD 7.5.9 용어 표).
 *
 * ## ⛔ D1 에 새 표도 새 칸도 두지 않는다. 마이그레이션 0건이다
 *
 * **정본은 `JobEvent` 한 줄**이다(`type = 'PRODUCT_CATEGORY_STATED'`). 그 표가 이미
 * **누가(`sellerId`) · 언제(`occurredAt`) · 어디서(`source`) · 무엇에서 무엇으로
 * (`beforeValue`/`afterValue`)** 를 들고 있고, `ON DELETE RESTRICT` 라 작업이 지워져도
 * 남는다. ⛔ **`DetailJob` 에 컬럼(플래그) 하나로 내지 않는다** — PRD `7.6.9b` 가 그 모양을
 * 금지했다. **필요한 것은 값이 아니라 「누가 언제 무엇을 진술했는가」** 이고, 컬럼으로 내면
 * **기록 없이 값만 도는 경로**가 열린다. 여기서는 **읽는 곳도 그 이벤트 줄 하나**라
 * 기록 없이 값이 존재할 수가 없다.
 *
 * ⛔ **`Consent` 표를 재사용하지 않았다.** 그 표는 **셀러 × 문서종류 × 판**이라 **작업에
 * 매이지 않고**(같은 셀러의 다른 작업이 같은 행을 공유한다), `docType` 에 CHECK 가 걸려
 * 있어 값을 늘리려면 **테이블 재작성 마이그레이션**이 든다. 상품 종류는 **작업마다 다른
 * 진술**이라 축이 맞지 않는다.
 */

import type { ActivitySource } from "./domain";

/** 셀러가 고르는 세 갈래. **값이 아니라 말이다** — 화면에 나가는 문장은 아래
 *  `PRODUCT_CATEGORY_STATEMENT_OPTIONS` 가 들고 있다.
 *
 * ⚠️ **고를 값의 목록은 아직 정해지지 않았다** `목록 미정`(PRD `7.6.9b` · 법무 `L-E6`
 * 선행조건 4). 법령상 「화장품」과 스마트스토어 카테고리 이름이 **같은 선을 긋지 않아**
 * 비누·핸드크림·디퓨저·차 계열이 그 틈에 있고, **목록이 그 경계와 어긋나면 어긋난 쪽으로
 * 새는 상품이 정확히 위험한 상품**이다. 여기 값을 늘릴 때 `productNameOffered` 의 판정이
 * **허용 목록**이라는 점을 함께 본다(그 함수 주석). */
export type ProductCategoryStatement = "NEITHER" | "COSMETIC" | "HEALTH_FUNCTIONAL_FOOD";

export interface ProductCategoryStatementOption {
  value: ProductCategoryStatement;
  /** ⭐ **셀러가 읽고 고르는 문장.** 목업 `A-26c`·`W-26` 정본 그대로다.
   *
   * ⛔ **여기에 「해당 없음」 꼴을 쓰지 마라** — 무엇에 해당하지 않는지가 안 보인다.
   * ⛔ **「확인했습니다」 꼴도 안 된다** — **읽었다**는 말이지 **진술**이 아니다. */
  sentence: string;
}

/** ⛔ **순서를 바꾸지 마라.** 목업 `A-26c` 가 이 순서로 서 있다.
 *
 * ⛔ **기본값을 골라 두지 않는다.** 이 목록 어디에도 `default` 가 없는 것이 그 뜻이다.
 * 미리 골라 두면 **셀러가 하지 않은 진술이 셀러 이름으로 남는다.** **진술은 고른 사람이
 * 있어야 진술이다**(PRD `7.6.9b` 판정 1). */
export const PRODUCT_CATEGORY_STATEMENT_OPTIONS: readonly ProductCategoryStatementOption[] =
  Object.freeze([
    Object.freeze({ value: "NEITHER" as const, sentence: "화장품도 건강기능식품도 아닙니다" }),
    Object.freeze({ value: "COSMETIC" as const, sentence: "화장품입니다" }),
    Object.freeze({
      value: "HEALTH_FUNCTIONAL_FOOD" as const,
      sentence: "건강기능식품입니다",
    }),
  ]);

/** `JobEvent.type`. ⛔ **이 문자열을 화면·라우트에 손으로 적지 마라** — 한 글자가 갈리면
 *  기록은 쌓이는데 **읽는 쪽이 못 찾아서** 진술이 통째로 없는 것처럼 보인다. */
export const PRODUCT_CATEGORY_STATEMENT_EVENT_TYPE = "PRODUCT_CATEGORY_STATED";

/** **한 번의 진술 행위 그 자체.** ⛔ **코드값 하나로 줄이지 마라** — 그러면 이 회차가 한
 *  일이 0 이 된다(PRD `7.6.9b`: "필요한 것은 값이 아니라 「누가 언제 무엇을 진술했는가」"). */
export interface ProductCategoryStatementRecord {
  statement: ProductCategoryStatement;
  /** ⭐ **셀러가 실제로 읽고 고른 문장.** 배너가 약속한 **「고르신 것」** 이 이 값이다.
   *
   * ⛔ **코드값에서 다시 만들어 내지 마라.** 문구를 고치는 날 **옛 기록이 하지 않은 말을
   * 하게 된다.** 저장된 문장을 그대로 읽어 온다. */
  sentence: string;
  /** ⭐ 배너가 약속한 **「시각」**. **D1 의 시계**(`JobEvent.occurredAt`)이고 기기 시계가
   *  아니다. 모양은 `YYYY-MM-DD HH:MM:SS`(UTC). */
  statedAt: string;
  /** 어디서 진술했는가. 앱·웹을 오가는 제품이라 기록에 필요하다. */
  statedFrom: ActivitySource;
  /** 바로 앞의 진술. **처음이면 `null`.**
   *
   * ⭐ **셀러가 진술을 바꿔도 앞의 진술이 사라지지 않는다** — `JobEvent` 는 덮어쓰기가
   * 없는 표라 **줄이 하나 더 쌓일 뿐**이고, 이 칸은 그 줄이 무엇을 바꿨는지를 말한다.
   * 덮어썼다면 이전 진술이 사라져 증거가 되지 못한다. */
  previousStatement: ProductCategoryStatement | null;
}

export function isProductCategoryStatement(value: unknown): value is ProductCategoryStatement {
  return PRODUCT_CATEGORY_STATEMENT_OPTIONS.some((option) => option.value === value);
}

/** 코드값이 가리키는 **지금 판**의 문장. ⛔ **저장된 기록을 되살릴 때 쓰지 마라**
 *  (`ProductCategoryStatementRecord.sentence` 주석). 화면이 **고르는 목록을 그릴 때**만
 *  쓴다. */
export function productCategoryStatementSentence(value: ProductCategoryStatement): string {
  return (
    PRODUCT_CATEGORY_STATEMENT_OPTIONS.find((option) => option.value === value)?.sentence ?? ""
  );
}

/** **상품명을 만들어 드리는가.** `A-50b` 가 서는 조건이 이 함수의 `false` 다.
 *
 * ## 진술이 없으면 막지 않는다 (`null` → `true`)
 *
 * ⭐ **오늘 도는 앱을 죽이지 않는 쪽이다.** 지금 올라오는 작업에는 진술이 한 건도 없고,
 * 막는 쪽으로 정하면 **어제까지 되던 상품명 만들기가 전부 선다** — 그것은 이 회차가 세우려던
 * 「상품명 만들기 하나만 막는다」가 아니라 **기능 전체를 끈 것**이다.
 *
 * ⛔ **그리고 그것은 법무가 세운 축과도 반대다.** 판별 근거가 **(ㄱ) 셀러 입력**으로
 * 정해졌고(`6.2.1`(2)ⓑ), 진술이 없는 작업은 **셀러가 화장품이라고 말한 적이 없는 작업**
 * 이다. 그 작업을 막으면 **우리가 추정해서 거절하는 것**이 되어 (ㄴ)으로 미끄러진다 —
 * 그쪽은 `L-E5`(2)(나)의 **「우리 관여가 능동적이다」** 를 키우고 **위양성(안 막아야 할
 * 셀러를 막는 것)** 을 새로 만든다.
 *
 * ⚠️ **대신 열린 채로 남는 것**: 진술을 안 한 셀러의 화장품은 지금도 상품명이 만들어진다
 * (`6.2.1`(2)ⓐ 의 **위음성**. **지금과 같은 상태**이지 이번 회차가 새로 연 구멍이 아니다).
 * 그 갈래를 닫으려면 **진술을 받기 전에는 상품명 만들기를 세워 두는** 쪽으로 가야 하고,
 * 그것은 「재촉하지 않는다」(`7.6.9b` 판정 3)와 정면으로 부딪친다 — **제품 결정이라 코드가
 * 고르지 않는다.**
 *
 * ## ⭐ 막는 쪽이 허용 목록이다
 *
 * `statement === "NEITHER"` 로 쓰고 **`!== "COSMETIC" && !== "HEALTH_FUNCTIONAL_FOOD"` 로
 * 쓰지 않는다.** 뒤엣것은 **값이 하나 늘 때 조용히 통과시킨다** — 그리고 값이 느는 이유는
 * 「목록이 법령 경계와 어긋나서」라, **새 값일수록 위험한 값**이다. */
export function productNameOffered(record: ProductCategoryStatementRecord | null): boolean {
  if (!record) return true;
  return record.statement === "NEITHER";
}

/** 셀러가 읽는 문장. **앱과 웹이 한 벌을 쓴다.**
 *
 * ⛔ **화면 파일에 한글을 다시 적지 마라.** 이 저장소에서 **같은 사실을 앱과 웹이 다르게
 * 말하게 된 자리가 세 번** 났고(PRD `7.7.3a` 의 `앱은 됐다 / 웹이 남았다`), 그때마다 두
 * 파일에 문자열이 따로 박혀 있었을 뿐이라 아무도 몰랐다.
 *
 * ⛔ **금지어**(`A-50b` 가 세우고 `A-26c` 가 같은 낱말로 간다): `제외 카테고리` ·
 * `금지 품목` · `취급 불가` · `차단` · `해당 없음` · `확인했습니다` · `필수` ·
 * `아직 안 고르셨습니다`. **전부 셀러를 잘못한 사람으로 만들고 상품 전체가 거부된 것으로
 * 읽힌다.** `이의 제기` · `소명` · `재심사` 도 쓰지 않는다 — 셋 다 **셀러가 심판받는 자리**
 * 로 읽히는데 **틀린 쪽은 우리 목록**이다. */
export const PRODUCT_CATEGORY_STATEMENT_COPY = Object.freeze({
  /** `A-26` 의 `이번 작업` 그룹 첫 줄 · `W-26` 오른쪽 패널 제목. */
  rowLabel: "상품 종류",
  /** 아직 고르지 않았을 때 그 줄의 오른쪽. ⛔ **`필수` 를 붙이지 않는다.** */
  rowUnstated: "고르기",
  sheetTitle: "어떤 상품인가요",
  /** ⭐ **막는 범위를 화면이 먼저 좁힌다.** 이 말이 없으면 셀러는 **상품을 못 올리는 줄**
   *  안다. */
  sheetBody:
    "화장품·건강기능식품은 표시·광고 규칙이 따로 있어 상품명을 만들어 드리지 않습니다. 배경 지우기와 내보내기는 그대로입니다.",
  /** ⭐ **코드가 지켜야 하는 약속이 이 한 줄이다.** 진술을 기록 없이 저장하면 이 문장이
   *  거짓말이 된다. */
  recordNotice: "고르신 것과 시각이 그대로 기록됩니다.",
  /** ⛔ **재촉하지 않는다.** 안 고른 대가는 **상품명을 직접 적으시는 것 하나**이고 이 줄이
   *  그것만 말한다. */
  skipNotice: "고르지 않으셔도 사진은 그대로 만들어 드립니다. 상품명만 직접 적으시게 됩니다.",
  close: "닫기",
  confirm: "고르기",
  /** 이미 진술한 뒤 다시 열었을 때의 주 버튼. 같은 값을 다시 고르는 것도 여기서 끝난다. */
  restate: "고치기",
  /** `A-50b`. ⭐ **왜 막혔는가.** 셀러가 **잘못한 것이 없다**는 것이 이 문장의 일이다. */
  blockedTitle: "이 상품은 상품명을 만들어 드리지 않습니다.",
  blockedBody:
    "표시·광고 규칙이 따로 있는 상품이라서입니다. 직접 적으시면 그대로 들어갑니다.",
  /** ⭐ **창구가 선택이 아니라 필수다**(`A-50b`). 고를 값의 목록 자체가 틀릴 수 있고,
   *  **틀린 쪽이 우리 목록일 때 셀러에게 출구가 있어야** 한다. `창구 미정`. */
  blockedAppeal: "이 상품이 아니라고 생각되시면 알려 주세요",
});

/** `PUT jobEndpoints.productCategoryStatement` 의 본문.
 *
 * ⛔ **문장을 함께 보내지 마라.** 서버가 공유 목록에서 꺼내 적는다 — 보내도 무시된다. */
export interface ProductCategoryStatementRequest {
  statement: ProductCategoryStatement;
}

/** 그 응답. ⛔ **`{ ok: true }` 로 줄이지 마라** — 화면이 **고르신 것과 시각**을 되비칠 수
 *  없으면 배너가 한 약속을 확인할 길이 없다. */
export interface ProductCategoryStatementResponse {
  productCategoryStatement: ProductCategoryStatementRecord;
}

/** 진술 한 줄이 이력(`A-12` 타임라인)에서 읽히는 모양. **`JobEvent.summary` 에 그대로
 *  저장된다** — 곧 **셀러가 나중에 읽는 글자**다. */
export function productCategoryStatementSummary(sentence: string): string {
  return `${PRODUCT_CATEGORY_STATEMENT_COPY.rowLabel} · ${sentence}`;
}
